// third-party
import { createSlice } from '@reduxjs/toolkit';
import { format, isValid, parse } from 'date-fns';

// project imports
import { ARYABHATTA_API_DOMAIN_V1 } from 'config';
import { AUTO_QUOTE, CUSTOMIZE, DEFAULT_ERROR_MESSAGE, HOTELS, OFFLINE_COMPONENT_URL, SIGHT_SEEING, TRANSFERS } from '../constant';
import axios from 'utils/axios';
import { dispatch } from '../index';
import { openSnackbar } from './snackbar';
import { ONLINE_TRANSFERS_CREATE_QUOTE, ONLINE_TRANSFERS_MODAL, SAVE_TRANSFERS } from 'views/pages/create-quote/EditAllTransfers/constants';
import { getFinalTransfersStr } from 'views/pages/create-quote/EditAllTransfers/utils';
import { setQuoteTransferFromFields } from './quotes';
import { extractErrorMessage } from 'views/pages/create-quote/utilities';

const initialState = {
    generateTransferButtonLoading: false,
    modalGenerateTransferLoading: false,
    errorState: {},
    generatedTransferData: {
        onlineTransfers: {
            quotePageTransferDetails: {
                apiData: {},
                createQuoteTransfers: []
            },
            modalPageTransferDetails: {
                apiData: {},
                onlineModalTransfersInitialFormValues: { data: {} }
            }
        }
    },
    isFormModiFied: false,
    isFormCustomizing: false
};
// this is the mapper for outer quotes page to show data
const getCreateQuotesTransferData = (data) => {
    const finalTransfers = data?.finalTransferDetails?.map((item) => ({
        origin: item?.routes?.[0]?.pickUpPoint,
        destination: item?.routes?.[item?.routes.length - 1]?.dropPoint,
        tCp: item?.tCp,
        finalPrice: item?.finalPrice
    }));
    return {
        transfers: finalTransfers,
        transfersTotalPrice: data?.transfersTotalPrice || 0,
        transferTotalCostPrice: data?.transfersTotalCostPrice
    };
};
const makeRouteLabels = (arr, isRouteAffectingPrice = false) => {
    if (arr?.length > 1) {
        if (isRouteAffectingPrice) {
            return arr?.reduce((prev, curr, idx) => {
                if (idx === 0) prev += `${curr?.origin} -> ${curr?.dropPoint} -> `;
                else if (idx === arr?.length - 1) prev += `${curr?.dropPoint}`;
                else prev += `${curr?.dropPoint} -> `;
                return prev;
            }, '');
        }
        return arr?.reduce((prev, curr, idx) => {
            if (idx === 0) prev += `${curr?.origin} -> ${curr?.destination} -> `;
            else if (idx === arr?.length - 1) prev += `${curr?.destination}`;
            else prev += `${curr?.destination} -> `;
            return prev;
        }, '');
    }
    return `${arr?.[0]?.origin} -> ${arr?.[0]?.destination}`;
};
export const FormatTravelDate = (date, expectedFormat = 'do-MMM-yyyy') => {
    try {
        const parsedDate = parse(date, expectedFormat, new Date());
        if (isValid(parsedDate)) {
            return format(parsedDate, 'yyyy-MM-dd');
        }
    } catch {
        return '';
    }
    return '';
};

const checkValidTransferType = (value = '', fallbackValue = '') => {
    if (typeof value === 'number') {
        return value;
    }
    return fallbackValue;
};

export const getRouteInfoSelectedRoute = (routes, travelDate = '', isRouteAffectingPrice = false, isEveryThingSelfBooked = false) => {
    let selectedRoute = '';
    let route = [];
    let availableRouteSelectionData = [];
    const findSelectedRouteItem = routes?.find((i) => i?.selected);
    const availableRouteDataTransformer = (data) => {
        if (data?.routes && Array.isArray(data?.routes) && data?.routes?.length > 0) {
            return data?.routes?.map((routeItem) => ({
                ...routeItem,
                pickUpRouteName: routeItem?.origin,
                dropRouteName: routeItem?.destination,
                origin: routeItem?.pickUpPoint,
                destination: routeItem?.dropPoint,
                isSelfBooked: routeItem?.isSelfBooked || isEveryThingSelfBooked,
                costPrice: isRouteAffectingPrice ? routeItem?.tCp : '-',
                sellingPrice: isRouteAffectingPrice ? routeItem?.finalPrice : '-',
                cabType: {
                    label: routeItem?.transferMode?.code === 'Self_Booked' ? 'Self Booked' : routeItem?.transferMode?.name,
                    value: routeItem?.transferMode?.code || ''
                },
                transferType: checkValidTransferType(routeItem?.transferType),
                dateOfTravel: FormatTravelDate(routeItem?.travelDate || ''),
                isRouteAffectingPrice
            }));
        }
        return [];
    };
    const transferMapper = (dataItem) => {
        if (dataItem && typeof dataItem === 'object' && Object?.keys(dataItem)?.length > 0) {
            return {
                transfer: dataItem?.tripType || 'One Way',
                originType: dataItem?.originType,
                destinationType: dataItem?.destinationType,
                origin: dataItem?.routes?.[0]?.pickUpPoint,
                destination: dataItem?.routes?.[dataItem?.routes?.length - 1]?.dropPoint,
                costPrice: dataItem?.tCp,
                sellingPrice: dataItem?.finalPrice,
                numberOfcabs: dataItem?.totalCabs || 0,
                transferType: dataItem?.transferType,
                cabType: {
                    label: dataItem?.modeCode === 'Self_Booked' ? 'Self Booked' : dataItem?.mode,
                    value: dataItem?.modeCode || ''
                },
                isRouteAffectingPrice: dataItem?.isRouteAffectingPrice,
                code: dataItem?.code,
                dateOfTravel: FormatTravelDate(dataItem?.routes?.[0]?.travelDate || '')
            };
        }
        return {};
    };
    if (findSelectedRouteItem) {
        route = availableRouteDataTransformer(findSelectedRouteItem);
        selectedRoute = makeRouteLabels(findSelectedRouteItem?.routes || [], isRouteAffectingPrice);
    }
    availableRouteSelectionData = routes?.map((item) => ({
        label: makeRouteLabels(item?.routes || [], isRouteAffectingPrice),
        value: makeRouteLabels(item?.routes || [], isRouteAffectingPrice),
        selectedRouteArr: availableRouteDataTransformer(item),
        updatedTransfers: transferMapper(item)
    }));
    return { selectedRoute, route, availableRouteSelectionData };
};

const getTransfersDataMapper = (data, isEveryThingSelfBooked = false) =>
    data?.map((dataItem) => ({
        transfer: dataItem?.tripType || 'One Way',
        originType: dataItem?.originType,
        isSelfBooked: isEveryThingSelfBooked || dataItem?.isSelfBooked,
        destinationType: dataItem?.destinationType,
        origin: dataItem?.routes?.[0]?.pickUpPoint,
        destination: dataItem?.routes?.[dataItem?.routes?.length - 1]?.dropPoint,
        costPrice: dataItem?.tCp,
        sellingPrice: dataItem?.finalPrice,
        numberOfcabs: dataItem?.totalCabs,
        transferType: dataItem?.transferType,
        cabType: { label: dataItem?.modeCode === 'Self_Booked' ? 'Self Booked' : dataItem?.mode, value: dataItem?.modeCode },
        isRouteAffectingPrice: dataItem?.isRouteAffectingPrice,
        code: dataItem?.code,
        dateOfTravel: FormatTravelDate(dataItem?.routes?.[0]?.travelDate || ''),
        ...getRouteInfoSelectedRoute(
            dataItem?.availableRoutes,
            FormatTravelDate(dataItem?.routes?.[0]?.travelDate || ''),
            dataItem?.isRouteAffectingPrice,
            isEveryThingSelfBooked
        )
    }));
const getCreateQuoteModaltransferData = (data) => {
    const isEveryThingSelfBooked = data.markAllTransfersSelfBooked || false;
    const finalRouteData = getFinalTransfersStr(data, isEveryThingSelfBooked);
    const transfers = getTransfersDataMapper(data?.finalTransferDetails || [], isEveryThingSelfBooked);
    return {
        pickUpPoint: { label: finalRouteData?.[0]?.pickUpPoint, value: finalRouteData?.[0]?.originCode },
        dropPoint: {
            label: finalRouteData?.[finalRouteData?.length - 1]?.dropPoint,
            value: finalRouteData?.[finalRouteData?.length - 1]?.destinationCode
        },
        transferType: data?.finalTransferType,
        cabType: { label: data?.transferMode?.name, value: data?.transferMode?.code },
        numberOfCabs: data?.numberOfCabs,
        transfers,
        totalSellingPrice: data?.transfersTotalPrice || 0,
        totalCostPrice: data?.transfersTotalCostPrice || 0,
        markAllTransfersSelfBooked: isEveryThingSelfBooked
    };
};

const slice = createSlice({
    name: 'transfers',
    initialState,
    reducers: {
        setIsFormModified(state, action) {
            return {
                ...state,
                isFormModiFied: action?.payload || false
            };
        },
        setIsGeneratingTrasnferLoading(state, action) {
            return {
                ...state,
                generateTransferButtonLoading: action?.payload || false
            };
        },
        setIsModalGeneratetrasnsferLoading(state, action) {
            return {
                ...state,
                modalGenerateTransferLoading: action?.payload || false
            };
        },
        setTransferData(state, action) {
            const type = action.payload.type;
            const destinationCode = action.payload?.destinationCode;
            const tabValue = action.payload?.tabValue;
            switch (type) {
                case ONLINE_TRANSFERS_CREATE_QUOTE: {
                    const createQuoteDataObject = getCreateQuotesTransferData(action.payload.data, destinationCode);
                    const ModalDataObject = getCreateQuoteModaltransferData(action.payload.data, destinationCode);
                    const apiData = action.payload.data;
                    const prefilledValue = state?.generatedTransferData?.[tabValue]?.onlineTransfers || {};
                    return {
                        ...state,
                        generatedTransferData: {
                            ...state.generatedTransferData,
                            [tabValue]: {
                                onlineTransfers: {
                                    ...prefilledValue,
                                    quotePageTransferDetails: {
                                        apiData,
                                        createQuoteTransfers: createQuoteDataObject
                                    },
                                    modalPageTransferDetails: {
                                        apiData,
                                        onlineModalTransfersInitialFormValues: { data: { ...ModalDataObject } }
                                    }
                                }
                            }
                        }
                    };
                }
                case ONLINE_TRANSFERS_MODAL: {
                    const ModalDataObject = getCreateQuoteModaltransferData(action.payload.data, destinationCode);
                    const apiData = action.payload.data;
                    const prefilledValue = state?.generatedTransferData?.[tabValue]?.onlineTransfers || {};

                    return {
                        ...state,
                        generatedTransferData: {
                            ...state.generatedTransferData,
                            [tabValue]: {
                                onlineTransfers: {
                                    ...prefilledValue,
                                    modalPageTransferDetails: {
                                        apiData,
                                        onlineModalTransfersInitialFormValues: { data: { ...ModalDataObject } }
                                    }
                                }
                            }
                        }
                    };
                }
                case SAVE_TRANSFERS: {
                    const createQuoteDataObject = getCreateQuotesTransferData(action.payload.data, destinationCode);
                    const apiData = action.payload.data;
                    const prefilledValue = state?.generatedTransferData?.[tabValue]?.onlineTransfers || {};

                    return {
                        ...state,
                        generatedTransferData: {
                            ...state.generatedTransferData,
                            [tabValue]: {
                                onlineTransfers: {
                                    ...prefilledValue,
                                    quotePageTransferDetails: {
                                        apiData,
                                        createQuoteTransfers: createQuoteDataObject
                                    }
                                }
                            }
                        }
                    };
                }
                default:
                    return {
                        ...state
                    };
            }
        },
        setIsCustomizeLoading(state, action) {
            return {
                ...state,
                isFormCustomizing: action?.payload || false
            };
        },
        resetState() {
            return initialState;
        }
    }
});

export default slice.reducer;
export const {
    setIsFormModified,
    setIsGeneratingTrasnferLoading,
    setTransferData,
    setIsCustomizeLoading,
    resetState,
    setIsModalGeneratetrasnsferLoading
} = slice.actions;

export const getTransfersDetails = (
    destinationCode = '',
    requestBody = {},
    type = ONLINE_TRANSFERS_CREATE_QUOTE,
    setDisabledSaveButton = () => {},
    quotePageFlow = true,
    tabValue,
    setTransferFieldsInput = () => {}
) => {
    const postUrl = `${ARYABHATTA_API_DOMAIN_V1}${AUTO_QUOTE}/${TRANSFERS}/${destinationCode}`;
    return async () => {
        try {
            if (type === ONLINE_TRANSFERS_MODAL) {
                dispatch(setIsModalGeneratetrasnsferLoading(true));
            } else {
                dispatch(setIsGeneratingTrasnferLoading(true));
            }
            const apiResponse = await axios.post(postUrl, requestBody, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { data: transferData = {} } = data || {};
            dispatch(
                setTransferData({
                    type,
                    data: transferData,
                    destinationCode,
                    tabValue
                })
            );
            const getFlattenRoutes = getFinalTransfersStr(transferData);
            const setTransferFieldValues = {
                pickup_from: getFlattenRoutes?.[0]?.pickUpPoint,
                drop_at: getFlattenRoutes?.[getFlattenRoutes?.length - 1]?.dropPoint,
                cab_type: transferData?.transferMode?.name,
                number_of_cabs: transferData?.numberOfCabs
            };
            setTransferFieldsInput(setTransferFieldValues);
            if (quotePageFlow) {
                dispatch(setQuoteTransferFromFields({ apiData: transferData, tabValue }));
            }

            setDisabledSaveButton(false);
        } catch (error) {
            const errorResponse = error.response || {}; // Access the error response
            const errorData = errorResponse.data || {}; // Access the data in the error response
            const message = extractErrorMessage(errorData);
            dispatch(
                openSnackbar({
                    open: true,
                    message: message || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: false
                })
            );
            setDisabledSaveButton(true);
        } finally {
            if (type === ONLINE_TRANSFERS_MODAL) {
                dispatch(setIsModalGeneratetrasnsferLoading(false));
            } else {
                dispatch(setIsGeneratingTrasnferLoading(false));
            }
        }
    };
};

export const customizeTransfersForm = (destinationCode = '', requestBody = {}, setDisabledSaveButton = () => {}, tabValue) => {
    const postUrl = `${ARYABHATTA_API_DOMAIN_V1}${AUTO_QUOTE}/${TRANSFERS}/${destinationCode}/${CUSTOMIZE}`;
    return async () => {
        try {
            dispatch(setIsCustomizeLoading(true));
            const apiResponse = await axios.post(postUrl, requestBody, {
                headers: {
                    source: 'mweb'
                }
            });
            const { data = {} } = apiResponse || {};
            const { data: transferData = {} } = data || {};
            dispatch(
                setTransferData({
                    type: ONLINE_TRANSFERS_MODAL,
                    data: transferData,
                    destinationCode,
                    replaceInitialFormValues: false,
                    tabValue
                })
            );
            setDisabledSaveButton(false);
        } catch (error) {
            const errorResponse = error.response || {}; // Access the error response
            const errorData = errorResponse.data || {}; // Access the data in the error response
            const message = extractErrorMessage(errorData);
            dispatch(
                openSnackbar({
                    open: true,
                    message: message || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: { color: 'error' },
                    close: false
                })
            );
            setDisabledSaveButton(true);
        } finally {
            dispatch(setIsCustomizeLoading(false));
        }
    };
};

export const saveTransferDetails =
    (apiData, destinationCode, tabValue, onClose, resetForm, setTransferFieldsInput = () => {}) =>
    async () => {
        try {
            dispatch(
                setTransferData({
                    type: SAVE_TRANSFERS,
                    data: apiData,
                    destinationCode,
                    tabValue
                })
            );
            const getFlattenRoutes = getFinalTransfersStr(apiData);
            const setTransferFieldValues = {
                pickup_from: getFlattenRoutes?.[0]?.pickUpPoint,
                drop_at: getFlattenRoutes?.[getFlattenRoutes?.length - 1]?.dropPoint,
                cab_type: apiData?.transferMode?.name,
                number_of_cabs: apiData?.numberOfCabs
            };
            setTransferFieldsInput(setTransferFieldValues);
            dispatch(setQuoteTransferFromFields({ apiData, tabValue }));
            onClose();
            resetForm();
        } catch (err) {
            console.error(err);
        }
    };
